import { z } from "zod";

import type { KlingDirectVideoPayload } from "../types";
import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { KLING2_6_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const kling26ProParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16", "1:1"]).default("9:16"),
    duration: z.enum(["5", "10"]).default("5"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const kling26Pro = defineModel({
  key: "kling_2_6_pro",
  stepType: "video",
  spec: {
    label: "Kling 2.6 Pro",
    description:
      "High-quality video generation with text-to-video and image-to-video support",
    icon: "kling-icon.svg",
    tags: ["video-gen"],
    featured: true,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      aspectRatio: true,
      duration: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Input Image",
          slot: "input_image",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
          { label: "1:1", value: "1:1" },
        ],
      },
      duration: {
        items: [
          { label: "5s", value: "5" },
          { label: "10s", value: "10" },
        ],
      },
    },
    params: kling26ProParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/kling-video/v2.6/pro/text-to-video",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 70_000 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "klingai:kling-video@2.6-pro",
      estimateCost(params) {
        return 0.07 * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP);
      },
      costPerSecond: 0.07 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const inputImageUrl = params.attachments?.find(
          (attachment) => attachment.slot === "input_image",
        )?.data;

        if (params.prompt?.length === 0) {
          throw new ValidationError(
            "Please enter a prompt to generate a video.",
          );
        }

        const size =
          KLING2_6_PRO_RESOLUTION_TO_WIDTH_HEIGHT_MAP[params.aspectRatio];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          duration: Number(params.duration),
          ...(inputImageUrl
            ? { inputs: { frameImages: [inputImageUrl] } }
            : { width: size.width, height: size.height }),
        };
      },
    },
    {
      provider: "kling",
      modelId: "kling-v2-6",
      estimateCost(params) {
        return 0.1 * 1_000_000 * Number(params.duration) * (1 + BRAND_MARKUP);
      },
      costPerSecond: 0.1 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params): KlingDirectVideoPayload {
        const inputImageUrl = params.attachments?.find(
          (att) => att.slot === "input_image",
        )?.data;

        if (params.prompt?.length === 0) {
          throw new ValidationError(
            "Please enter a prompt to generate a video.",
          );
        }

        return {
          model_name: "kling-v2-6",
          prompt: params.prompt,
          aspect_ratio: params.aspectRatio,
          duration: params.duration,
          mode: "pro",
          ...(inputImageUrl ? { image_url: inputImageUrl } : {}),
        };
      },
    },
  ],
});
